/**
 * TTS 音声キャッシュ管理（パフォーマンス）
 */

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import {
  getTtsCacheDashboard,
  getTtsCacheEntries,
  clearAllTtsCache,
  clearTtsCacheStatsOnly,
  runTtsCacheEviction,
  updateTtsCacheConfig,
} from '../../lib/admin-api';
import { useToast } from '../../hooks/use-toast';
import { Loader2, RefreshCw, Trash2, Database, Gauge, ListOrdered, ChevronLeft, ChevronRight } from 'lucide-react';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { useState, useEffect } from 'react';

function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

export default function TtsCache() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [clearAllOpen, setClearAllOpen] = useState(false);
  const [clearStatsOpen, setClearStatsOpen] = useState(false);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['admin', 'tts-cache', 'dashboard'],
    queryFn: getTtsCacheDashboard,
    retry: 1,
  });

  const [entriesPage, setEntriesPage] = useState(0);
  const [entriesPageSize, setEntriesPageSize] = useState(50);

  const dbOkForEntries = Boolean(data && data.dbAvailable !== false);

  const {
    data: entriesData,
    isLoading: entriesLoading,
    isFetching: entriesFetching,
    refetch: refetchEntries,
  } = useQuery({
    queryKey: ['admin', 'tts-cache', 'entries', entriesPage, entriesPageSize],
    queryFn: () =>
      getTtsCacheEntries({
        limit: entriesPageSize,
        offset: entriesPage * entriesPageSize,
      }),
    enabled: dbOkForEntries,
    retry: 1,
    placeholderData: keepPreviousData,
  });

  const entriesTotal = entriesData?.total ?? 0;
  const entriesOffset = entriesData?.offset ?? 0;
  const entryRows = entriesData?.entries ?? [];
  const maxPage = Math.max(0, Math.ceil(entriesTotal / entriesPageSize) - 1);

  useEffect(() => {
    if (entriesPage > maxPage) setEntriesPage(maxPage);
  }, [entriesPage, maxPage]);

  const [form, setForm] = useState({
    maxDiskBytes: '',
    idleDays: '',
    evictionIntervalMs: '',
  });

  useEffect(() => {
    if (!data?.config) return;
    setForm({
      maxDiskBytes: String(data.config.maxDiskBytes ?? ''),
      idleDays: String(data.config.idleDays ?? ''),
      evictionIntervalMs: String(data.config.evictionIntervalMs ?? ''),
    });
  }, [data?.config]);

  const saveConfigMutation = useMutation({
    mutationFn: () =>
      updateTtsCacheConfig({
        maxDiskBytes: form.maxDiskBytes === '' ? undefined : parseInt(form.maxDiskBytes, 10),
        idleDays: form.idleDays === '' ? undefined : parseInt(form.idleDays, 10),
        evictionIntervalMs:
          form.evictionIntervalMs === '' ? undefined : parseInt(form.evictionIntervalMs, 10),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'tts-cache'] });
      toast({ title: '保存しました', description: '退避設定を更新しました' });
    },
    onError: (e: Error) => {
      toast({ title: 'エラー', description: e.message, variant: 'destructive' });
    },
  });

  const clearAllMutation = useMutation({
    mutationFn: clearAllTtsCache,
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'tts-cache'] });
      toast({ title: '完了', description: r?.message ?? '全削除しました' });
      setClearAllOpen(false);
    },
    onError: (e: Error) => {
      toast({ title: 'エラー', description: e.message, variant: 'destructive' });
    },
  });

  const clearStatsMutation = useMutation({
    mutationFn: clearTtsCacheStatsOnly,
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'tts-cache'] });
      toast({ title: '完了', description: r?.message ?? '統計をクリアしました' });
      setClearStatsOpen(false);
    },
    onError: (e: Error) => {
      toast({ title: 'エラー', description: e.message, variant: 'destructive' });
    },
  });

  const evictionMutation = useMutation({
    mutationFn: runTtsCacheEviction,
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'tts-cache'] });
      const x = r.result;
      toast({
        title: '退避ジョブ完了',
        description: `孤立(ディスク) ${x.diskOrphansRemoved} / 孤立(DB) ${x.dbOrphansRemoved} / 退避 ${x.evictedCount} / 解放約 ${formatBytes(x.bytesFreedApprox)}`,
      });
    },
    onError: (e: Error) => {
      toast({ title: 'エラー', description: e.message, variant: 'destructive' });
    },
  });

  const errMsg = error instanceof Error ? error.message : String(error);
  /** サーバが明示した DB 未利用（マイグレーション未適用など）。undefined は従来互換で「利用可能」とみなす */
  const dbUnavailable = data?.dbAvailable === false;

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-6xl">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-gray-900 flex items-center gap-2">
              <Gauge className="w-8 h-8 text-medical-blue" />
              TTSキャッシュ
            </h1>
            <p className="text-base text-gray-500 mt-2">
              音声合成結果のディスクキャッシュと集計。サーバの{' '}
              <code className="text-base bg-gray-100 px-1 rounded">TTS_CACHE_*</code> 環境変数と連動します。
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void refetch();
              if (dbOkForEntries) void refetchEntries();
            }}
            disabled={isFetching || (dbOkForEntries && entriesFetching)}
            className="h-9 rounded-lg border-gray-200"
          >
            {isFetching || (dbOkForEntries && entriesFetching) ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            <span className="ml-2">再読み込み</span>
          </Button>
        </div>

        {error && !data && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 shadow-sm text-base text-amber-900">
            <strong>取得できませんでした:</strong> {errMsg}
            <p className="mt-2 text-amber-800">
              TTSキャッシュが未初期化（503）の場合はサーバで <code>CachingTTS</code> が登録されているか、DB
              マイグレーション <code>create_tts_cache_20260330.sql</code> が適用されているか確認してください。
            </p>
          </div>
        )}

        {data && dbUnavailable && (
          <div className="rounded-2xl border border-amber-300 bg-amber-50 p-6 shadow-sm text-base text-amber-950">
            <strong>DB 集計・設定が利用できません。</strong>
            ディスク使用量とプロセス内カウンタのみ表示しています。マイグレーション{' '}
            <code className="text-base bg-amber-100/80 px-1 rounded">database/migrations/create_tts_cache_20260330.sql</code>{' '}
            を適用し、サーバを再起動してください。
            {data.dbError ? (
              <p className="mt-2 font-mono text-base text-amber-900/90 break-all">{data.dbError}</p>
            ) : null}
          </div>
        )}

        {isLoading && !data && (
          <div className="flex items-center gap-2 text-base text-gray-700">
            <Loader2 className="w-5 h-5 animate-spin" />
            読み込み中…
          </div>
        )}

        {data && (
          <>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                <div className="text-2xl font-bold text-gray-900 mb-1 flex items-center gap-2">
                  <Database className="w-5 h-5" />
                  ディスク
                </div>
                <p className="text-base text-gray-500 mb-4">ベースディレクトリと使用量（概算）</p>
                <div className="space-y-2 text-base">
                  <div>
                    <span className="text-gray-500">パス</span>
                    <p className="font-mono text-base break-all mt-1 text-gray-900">{data.baseDir}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">概算バイト数</span>
                    <p className="text-xl font-semibold text-gray-900 tabular-nums">{formatBytes(data.diskBytesApprox)}</p>
                  </div>
                </div>
              </div>

              <div className={`rounded-2xl border border-gray-200 bg-white p-6 shadow-sm ${dbUnavailable ? 'opacity-90' : ''}`}>
                <div className="text-2xl font-bold text-gray-900 mb-1">ライフタイム集計（DB）</div>
                <p className="text-base text-gray-500 mb-4">
                  {dbUnavailable ? 'DB に接続できないため表示できません' : 'ヒット・保存の累計'}
                </p>
                <div className="grid grid-cols-2 gap-4 text-base">
                  <div>
                    <span className="text-gray-500">ヒット</span>
                    <p className="text-2xl font-semibold text-gray-900 tabular-nums">
                      {dbUnavailable ? '—' : data.lifetimeHits.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">保存</span>
                    <p className="text-2xl font-semibold text-gray-900 tabular-nums">
                      {dbUnavailable ? '—' : data.lifetimeStores.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="text-2xl font-bold text-gray-900 mb-4">プロセス内カウンタ（再起動でリセット）</div>
              <div className="flex gap-8 text-base">
                <div>
                  <span className="text-gray-500">ヒット</span>
                  <p className="text-xl font-semibold text-gray-900 tabular-nums">{data.processCounters.hits.toLocaleString()}</p>
                </div>
                <div>
                  <span className="text-gray-500">保存</span>
                  <p className="text-xl font-semibold text-gray-900 tabular-nums">{data.processCounters.stores.toLocaleString()}</p>
                </div>
              </div>
            </div>

            {!dbUnavailable && (
              <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                <div className="text-2xl font-bold text-gray-900 mb-1 flex items-center gap-2">
                  <ListOrdered className="w-5 h-5" />
                  キー別ヒット数ランキング
                </div>
                <p className="text-base text-gray-500 mb-4">
                  DB に記録されたテキストプレビュー単位の累計ヒット数です。多い順に表示し、ページ分割とスクロールで大量件でも扱いやすくしています。
                </p>
                <div className="space-y-3">
                  {entriesData?.dbAvailable === false && (
                    <p className="text-base text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                      一覧を取得できませんでした。{entriesData.dbError ? `（${entriesData.dbError}）` : ''}
                    </p>
                  )}
                  {entriesLoading && !entriesData ? (
                    <div className="flex items-center gap-2 text-base text-gray-700 py-8 justify-center">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      一覧を読み込み中…
                    </div>
                  ) : (
                    <>
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-base">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-gray-700">1ページあたり</span>
                          <Select
                            value={String(entriesPageSize)}
                            onValueChange={(v) => {
                              setEntriesPageSize(Number(v));
                              setEntriesPage(0);
                            }}
                          >
                            <SelectTrigger className="w-[100px] h-9 rounded-xl border-gray-200">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="25">25 件</SelectItem>
                              <SelectItem value="50">50 件</SelectItem>
                              <SelectItem value="100">100 件</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <p className="text-gray-700 tabular-nums">
                          {entriesTotal === 0
                            ? '0 件'
                            : `${entriesOffset + 1}–${entriesOffset + entryRows.length} / 全 ${entriesTotal.toLocaleString()} 件`}
                        </p>
                      </div>

                      <ScrollArea className="h-[min(60vh,520px)] w-full rounded-md border">
                        <Table>
                          <TableHeader>
                            <TableRow className="border-b border-gray-200 hover:bg-transparent">
                              <TableHead className="text-base font-bold text-gray-700 w-14 text-right sticky top-0 bg-background z-10 shadow-sm">
                                順位
                              </TableHead>
                              <TableHead className="text-base font-bold text-gray-700 min-w-[200px] sticky top-0 bg-background z-10 shadow-sm">
                                テキスト（プレビュー）
                              </TableHead>
                              <TableHead className="text-base font-bold text-gray-700 w-28 text-right sticky top-0 bg-background z-10 shadow-sm">
                                ヒット数
                              </TableHead>
                              <TableHead className="text-base font-bold text-gray-700 w-24 text-right sticky top-0 bg-background z-10 shadow-sm">
                                概算
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {entryRows.length === 0 ? (
                              <TableRow className="border-b border-gray-100">
                                <TableCell colSpan={4} className="text-center text-base text-gray-500 py-12">
                                  表示するエントリがありません
                                </TableCell>
                              </TableRow>
                            ) : (
                              entryRows.map((row, i) => {
                                const rank = entriesOffset + i + 1;
                                const preview =
                                  row.textPreview?.trim() ||
                                  `（プレビューなし · ${row.hash.slice(0, 12)}…）`;
                                return (
                                  <TableRow key={row.hash} className="border-b border-gray-100 hover:bg-gray-50/50">
                                    <TableCell className="py-4 text-base text-right text-gray-700 tabular-nums align-top">
                                      {rank}
                                    </TableCell>
                                    <TableCell className="py-4 align-top max-w-0">
                                      <p
                                        className="text-base text-gray-900 break-words line-clamp-3"
                                        title={preview}
                                      >
                                        {preview}
                                      </p>
                                    </TableCell>
                                    <TableCell className="py-4 text-base text-right font-medium text-gray-900 tabular-nums align-top">
                                      {row.hitCount.toLocaleString()}
                                    </TableCell>
                                    <TableCell className="py-4 text-base text-right text-gray-700 tabular-nums align-top">
                                      {formatBytes(row.approxBytes)}
                                    </TableCell>
                                  </TableRow>
                                );
                              })
                            )}
                          </TableBody>
                        </Table>
                      </ScrollArea>

                      <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={entriesPage <= 0 || entriesFetching}
                          onClick={() => setEntriesPage((p) => Math.max(0, p - 1))}
                          className="h-9 rounded-lg border-gray-200"
                        >
                          <ChevronLeft className="w-4 h-4 mr-1" />
                          前のページ
                        </Button>
                        <span className="text-base text-gray-700 tabular-nums">
                          ページ {entriesPage + 1} / {Math.max(1, maxPage + 1)}
                        </span>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={entriesPage >= maxPage || entriesFetching}
                          onClick={() => setEntriesPage((p) => Math.min(maxPage, p + 1))}
                          className="h-9 rounded-lg border-gray-200"
                        >
                          次のページ
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="text-2xl font-bold text-gray-900 mb-1">退避・容量ポリシー（DB）</div>
              <p className="text-base text-gray-500 mb-4">
                {dbUnavailable ? (
                  'マイグレーション適用後に編集できます。'
                ) : (
                  <>
                    上限 0 は無制限。退避ジョブ間隔 0 は定期ジョブなし（手動「退避実行」は可）。環境変数{' '}
                    <code className="text-base bg-gray-100 px-1">TTS_CACHE_EVICTION_INTERVAL_MS</code>{' '}
                    が優先されます。
                  </>
                )}
              </p>
              <div className="space-y-4 max-w-md">
                <div>
                  <Label htmlFor="maxDiskBytes" className="text-base font-bold text-gray-700">ディスク上限（バイト）</Label>
                  <Input
                    id="maxDiskBytes"
                    type="number"
                    min={0}
                    value={form.maxDiskBytes}
                    onChange={(e) => setForm((f) => ({ ...f, maxDiskBytes: e.target.value }))}
                    placeholder="0 = 無制限"
                    disabled={dbUnavailable}
                    className="h-11 rounded-xl border-gray-200 bg-white"
                  />
                </div>
                <div>
                  <Label htmlFor="idleDays" className="text-base font-bold text-gray-700">アイドル判定（日）</Label>
                  <Input
                    id="idleDays"
                    type="number"
                    min={1}
                    value={form.idleDays}
                    onChange={(e) => setForm((f) => ({ ...f, idleDays: e.target.value }))}
                    disabled={dbUnavailable}
                    className="h-11 rounded-xl border-gray-200 bg-white"
                  />
                </div>
                <div>
                  <Label htmlFor="evictionIntervalMs" className="text-base font-bold text-gray-700">退避ジョブ間隔（ms）</Label>
                  <Input
                    id="evictionIntervalMs"
                    type="number"
                    min={0}
                    value={form.evictionIntervalMs}
                    onChange={(e) => setForm((f) => ({ ...f, evictionIntervalMs: e.target.value }))}
                    placeholder="0 = ジョブなし"
                    disabled={dbUnavailable}
                    className="h-11 rounded-xl border-gray-200 bg-white"
                  />
                </div>
                <Button
                  onClick={() => saveConfigMutation.mutate()}
                  disabled={saveConfigMutation.isPending || dbUnavailable}
                  className="bg-gray-900 hover:bg-gray-800 text-white rounded-lg h-11 px-5 font-bold shadow-sm"
                >
                  {saveConfigMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  <span className="ml-2">設定を保存</span>
                </Button>
              </div>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="text-2xl font-bold text-gray-900 mb-1">操作</div>
              <p className="text-base text-gray-500 mb-4">危険な操作は確認ダイアログ後に実行されます。</p>
              <div className="flex flex-wrap gap-3">
                <Button
                  variant="secondary"
                  onClick={() => evictionMutation.mutate()}
                  disabled={evictionMutation.isPending || dbUnavailable}
                >
                  {evictionMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  <span className="ml-2">退避ジョブを今すぐ実行</span>
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setClearStatsOpen(true)}
                  disabled={dbUnavailable}
                  className="h-11 rounded-lg border-gray-200"
                >
                  永続集計のみクリア
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => setClearAllOpen(true)}
                  disabled={dbUnavailable}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  キャッシュ全削除（ディスク+DB）
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      <AlertDialog open={clearAllOpen} onOpenChange={setClearAllOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>TTSキャッシュをすべて削除しますか？</AlertDialogTitle>
            <AlertDialogDescription>
              ディスク上の音声・メタファイルと DB 集計、プロセス内カウンタがリセットされます。この操作は取り消せません。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>キャンセル</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => clearAllMutation.mutate()}
            >
              削除する
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={clearStatsOpen} onOpenChange={setClearStatsOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>永続集計のみクリアしますか？</AlertDialogTitle>
            <AlertDialogDescription>
              DB のキー別集計とライフタイム合計を消去します。ディスク上のファイルは残ります。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>キャンセル</AlertDialogCancel>
            <AlertDialogAction onClick={() => clearStatsMutation.mutate()}>実行</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
